import { useState } from "react";
import { HashRouter } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, Target, Users, TrendingUp, CheckCircle, Zap, ChevronDown, ChevronUp } from "lucide-react";
import "@/App.css";

import { CTAButton } from "./components/CTAButton";
import { YouTubeVideo } from "./components/YouTubeVideo";
import { SellCard } from "./components/SellCard";
import { TestimonialCard } from "./components/TestimonialCard";
import { VideoTestimonial } from "./components/VideoTestimonial";

// Configuration
const CONFIG = {
  programName: "ASA",
  fullName: "Advanced Scaling Academy",
  logoUrl: "https://customer-assets.emergentagent.com/job_7213cd92-a4c8-4cc6-b6c9-666cbacc2cda/artifacts/gulq6hqd_Logo%20ASA%203%20%28full%20white%29.png",
  youtubeVideoId: "wOXzHWtbhqM",
  diagnosticUrl: "https://raphael-coach-1.emergent.host",
  stats: {
    clients: "500+",
    rating: "5/5",
    results: "100%"
  }
};

// Testimonial images - Tous les témoignages (visibles de base)
const TESTIMONIAL_IMAGES_INITIAL = [
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/blaf1y56_Capture%20d%E2%80%99e%CC%81cran%202026-01-23%20a%CC%80%2016.04.36.png",
    alt: "5 ventes en 5 minutes - Témoignage ASA"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/uuk5ci1u_Capture%20d%E2%80%99e%CC%81cran%202026-01-23%20a%CC%80%2016.20.33.png",
    alt: "Première vente ASA - Stripe notification"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/xytvlcjm_Capture%20d%E2%80%99e%CC%81cran%202026-01-23%20a%CC%80%2016.20.40.png",
    alt: "Multiple ventes notification - ASA"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/j986km3s_Capture%20d%E2%80%99e%CC%81cran%202026-01-23%20a%CC%80%2016.20.44.png",
    alt: "Dashboard revenus - 17 ans entrepreneur"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/q9xy1a5y_Capture%20d%E2%80%99e%CC%81cran%202026-01-23%20a%CC%80%2016.20.47.png",
    alt: "Première vente Beacons - ASA"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/51ohiddm_Capture%20d%E2%80%99e%CC%81cran%202026-01-23%20a%CC%80%2016.20.55.png",
    alt: "S4turne - 2 ventes en 30 min ASA"
  }
];

// Témoignages supplémentaires (affichés quand on clique "En savoir plus")
const TESTIMONIAL_IMAGES_MORE = [
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/krznittl_Capture%20d%E2%80%99e%CC%81cran%202026-01-23%20a%CC%80%2016.23.18.png",
    alt: "Yanis - Multiple ventes ASA pendant chill"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/s9a52nkq_Capture%20d%E2%80%99e%CC%81cran%202026-01-23%20a%CC%80%2016.23.22.png",
    alt: "mth_digtl - Vente ASA Tour Eiffel"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/4eqigohf_Capture%20d%E2%80%99e%CC%81cran%202026-01-23%20a%CC%80%2016.23.33.png",
    alt: "Yassine - Ventes ASA 397€"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/8ttfki34_Capture%20d%E2%80%99e%CC%81cran%202026-01-23%20a%CC%80%2016.23.45.png",
    alt: "Adrien - 3200€ de ventes ASA"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/wb5lqviz_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.11.23.png",
    alt: "GD - Vente 497€ Formation ASA"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/m035ccq2_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.11.29.png",
    alt: "795€ de ventes - Pas la meilleure journée"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/i990tw41_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.11.50.png",
    alt: "BT - 10 252$ Payouts Beacons"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/o4nyv9xt_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.12.01.png",
    alt: "Première vente ASA 5 min après lancement"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/n5uqskrh_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.12.17.png",
    alt: "LE - 15 ans, 4133$ - Dubai fin d'année"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/kzvvvnwd_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.12.05.png",
    alt: "4863$ - C'est que le début avec l'ASA"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/vepi6kc7_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.12.12.png",
    alt: "19 606$ - Petit lancement sympa"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/20vhuys4_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.12.21.png",
    alt: "812€ - 2 ventes la première journée"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/n6qs6s7b_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.12.26.png",
    alt: "Vente en auto - ASA"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/c7mg1eqc_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.12.30.png",
    alt: "2,2k en 48h à 17 ans"
  },
  // Nouveaux témoignages
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/zq03e565_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.13.43.png",
    alt: "Première vente 248€ - Lâchez rien c'est du sérieux"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/20wef8cb_Capture%20d%E2%80%99e%CC%81cran%202026-02-18%20a%CC%80%2019.16.45.png",
    alt: "Lohan - 1878$ vente tous les jours"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/svltybe1_Capture%20d%E2%80%99e%CC%81cran%202026-02-18%20a%CC%80%2019.17.03.png",
    alt: "7468€ - Vente 497€ grâce à ma vidéo"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/3vd90lfw_Capture%20d%E2%80%99e%CC%81cran%202026-02-18%20a%CC%80%2019.20.10.png",
    alt: "1500€ cette semaine à 13 ans"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/d7vvizju_Capture%20d%E2%80%99e%CC%81cran%202026-02-18%20a%CC%80%2019.20.24.png",
    alt: "S4turne - 2099€ petite vente ASA trop booster"
  },
  // Nouveaux témoignages batch 3
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/4pbjvmwg_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.12.50.png",
    alt: "YF - 4ème vente 397€ automatisée"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/zm7s8gti_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.12.58.png",
    alt: "Vente ASA 397€ depuis la voiture"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/9onokaxb_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.13.08.png",
    alt: "LM - LET'S GO vente 397€"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/7jg5aqkr_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.13.15.png",
    alt: "Vente en 2-3j - lâchez pas"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/u35vo62b_Capture%20d%E2%80%99e%CC%81cran%202026-02-05%20a%CC%80%2023.13.20.png",
    alt: "CG - Enfin! Vente 397€"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/wu0fvjx4_Journe%CC%81e%201k%20Sunset%20-%20Te%CC%81moignages%20Vide%CC%81os.png",
    alt: "Journée 1k Sunset - Multiple ventes Beacons"
  }
];

// Vidéos témoignages
const VIDEO_TESTIMONIALS = [
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/e56aozg1_tom.mov",
    title: "Tom",
    subtitle: "11 000€ généré en seulement 1 mois",
    proofImages: [
      "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/p4w4t8i1_78DA74A2-DD4E-459D-94B9-3F2A90FE9CCC.JPG",
      "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/kn7ylkgu_Capture%20d%E2%80%99e%CC%81cran%C2%A0.%202026-04-03%20a%CC%80%2002.35.12.png",
      "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/8i07f591_WhatsApp%20Image%202026-04-03%20at%2002.37.39.jpeg"
    ]
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/10u8h7tp_ethan.mov",
    title: "Ethan",
    subtitle: "10k€ en 2 mois à 13 ans"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/2860un3l_Copie%20de%202bb3157584ab46328de6ca78bc47db6d.mov",
    title: "Lissandro",
    subtitle: "15 ans - 6 000€"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/e7c7xhvd_Copie%20de%203910C6F9-E6BF-4344-9605-2B61A81B8A6C.MOV",
    title: "Yanis",
    subtitle: "18 ans - Start 2K et 7 000€/mois"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/9ssbqc0m_Copie%20de%208062022651262797060.mp4",
    title: "Mathis",
    subtitle: "15 ans - 500€ et 300€/jour"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/4nv7wtyx_Copie%20de%20dji_mimo_20260218_213336_20260218213312_1771446872907_video.mov",
    title: "Theo",
    subtitle: "2 000€/mois"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_shred-academy-clone/artifacts/or9c8lpu_Copie%20de%20IMG_1292.MP4",
    title: "Saturne",
    subtitle: "Elle a généré 10K à seulement 18 ans"
  }
];

// Vidéos YouTube avec titres (2 vidéos) - CORRIGÉS
const YOUTUBE_VIDEOS = [
  {
    id: "4gJwIUPJFz0",
    title: "Mon histoire - De 0 à 100 000€"
  },
  {
    id: "6rN5IRXV76Q",
    title: "Témoignage avec Moha - 40 000€ à 16 ans"
  }
];

// Sell cards content - Pourquoi avoir le diagnostic gratuit
const SELL_CARDS = [
  {
    icon: Rocket,
    title: "Aide 100% Gratuite",
    description: "Le diagnostic gratuit est là pour t'aider sans aucun engagement. On analyse ta situation et on te donne des conseils concrets."
  },
  {
    icon: Target,
    title: "Plan Personnalisé",
    description: "Tu vas recevoir un plan d'action personnalisé. Rafael va lire ton diagnostic et créer une stratégie adaptée à ta situation."
  },
  {
    icon: TrendingUp,
    title: "Call avec Rafael",
    description: "Si t'as de la chance, tu peux décrocher un call avec Rafael, expert en marketing digital qui a généré +150k€ à 17 ans."
  }
];

// Hero Section
const HeroSection = () => (
  <section 
    data-testid="hero-section"
    className="min-h-[70vh] flex flex-col items-center justify-center text-center pt-10 pb-10 relative overflow-hidden px-6"
  >
    {/* Background effect */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[radial-gradient(circle,rgba(0,163,255,0.08)_0%,transparent_70%)] pointer-events-none" />
    
    {/* Logo */}
    <motion.img
      data-testid="hero-logo"
      src={CONFIG.logoUrl}
      alt={`${CONFIG.programName} Logo`}
      className="w-32 md:w-40 mb-8"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    />

    {/* Headline */}
    <motion.h1
      data-testid="hero-headline"
      className="text-2xl md:text-4xl lg:text-5xl font-black tracking-tight text-white max-w-5xl mb-4 leading-tight"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      Obtiens ton <span className="text-[#00A3FF]">diagnostic gratuit</span>
      <br />
      pour que j'analyse PERSONNELLEMENT
      <br />
      ton BUSINESS
    </motion.h1>

    {/* Sub-headline urgence */}
    <motion.p
      className="text-lg md:text-xl text-red-500 font-bold mb-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.25 }}
    >
      Disponible seulement pour 48H
    </motion.p>

    {/* Subtitle */}
    <motion.p
      data-testid="hero-subtitle"
      className="text-base md:text-lg text-gray-400 max-w-2xl mb-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
    >
      <span className="text-[#00A3FF] font-semibold">Important :</span> Regarde les vidéos en dessous pour découvrir comment nos membres génèrent des revenus en ligne grâce à notre méthode
    </motion.p>

    {/* Stats */}
    <motion.div
      data-testid="hero-stats"
      className="flex flex-wrap items-center justify-center gap-4 md:gap-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
    >
      <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full">
        <Users className="w-5 h-5 text-[#00A3FF]" />
        <span className="text-[#00A3FF] font-bold">{CONFIG.stats.clients}</span>
        <span className="text-gray-400">clients</span>
      </div>
      <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full">
        <CheckCircle className="w-5 h-5 text-[#00A3FF]" />
        <span className="text-[#00A3FF] font-bold">{CONFIG.stats.rating}</span>
        <span className="text-gray-400">satisfaction</span>
      </div>
      <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full">
        <Zap className="w-5 h-5 text-[#00A3FF]" />
        <span className="text-[#00A3FF] font-bold">{CONFIG.stats.results}</span>
        <span className="text-gray-400">résultats</span>
      </div>
    </motion.div>
  </section>
);

// VSL Section
const VSLSection = () => (
  <section 
    data-testid="vsl-section"
    className="py-10 md:py-16 px-6"
  >
    <div className="max-w-4xl mx-auto">
      <motion.h2
        data-testid="vsl-title"
        className="text-3xl md:text-4xl font-bold tracking-tight text-white text-center mb-10"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        Comment Rafael a généré{" "}
        <span className="text-[#00A3FF]">+150 000€</span> à 17 ans
      </motion.h2>

      <YouTubeVideo videoId={CONFIG.youtubeVideoId} title="ASA - Advanced Scaling Academy" />

      <motion.div
        className="flex justify-center mt-10"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <CTAButton href={CONFIG.diagnosticUrl}>
          Je veux mon diagnostic gratuit
        </CTAButton>
      </motion.div>
    </div>
  </section>
);

// Testimonials Section with "En savoir plus" button
const TestimonialsSection = () => {
  const [showMore, setShowMore] = useState(false);

  const displayedImages = showMore 
    ? [...TESTIMONIAL_IMAGES_INITIAL, ...TESTIMONIAL_IMAGES_MORE]
    : TESTIMONIAL_IMAGES_INITIAL;

  // Calculer le layout dynamiquement
  const rows = [];
  for (let i = 0; i < displayedImages.length; i += 3) {
    rows.push(displayedImages.slice(i, i + 3));
  }

  return (
    <section 
      data-testid="testimonials-section"
      className="py-20 md:py-32 px-6"
    >
      <div className="max-w-7xl mx-auto">
        <motion.h2
          data-testid="testimonials-title"
          className="text-3xl md:text-4xl font-bold tracking-tight text-white text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Les Résultats de Nos{" "}
          <span className="text-[#00A3FF]">Membres</span>
        </motion.h2>

        <motion.p
          className="text-lg text-gray-400 text-center mb-12 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Des résultats concrets, pas des promesses en l'air
        </motion.p>

        {/* Grid layout dynamique */}
        <div className="space-y-6 md:space-y-8">
          <AnimatePresence mode="sync">
            {rows.map((row, rowIndex) => (
              <motion.div 
                key={rowIndex}
                className={`grid gap-6 md:gap-8 ${
                  row.length === 3 
                    ? 'grid-cols-1 md:grid-cols-3' 
                    : row.length === 2 
                    ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto' 
                    : 'max-w-lg mx-auto'
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: rowIndex * 0.1 }}
              >
                {row.map((img, index) => (
                  <TestimonialCard 
                    key={`${rowIndex}-${index}`}
                    imageUrl={img.url}
                    alt={img.alt}
                    index={rowIndex * 3 + index}
                  />
                ))}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Bouton "En savoir plus" / "Voir moins" */}
        <motion.div
          className="flex justify-center mt-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <button
            data-testid="show-more-testimonials-btn"
            onClick={() => setShowMore(!showMore)}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-[#00A3FF]/50 text-[#00A3FF] font-semibold hover:bg-[#00A3FF]/10 transition-all duration-300"
          >
            {showMore ? (
              <>
                Voir moins
                <ChevronUp className="w-5 h-5" />
              </>
            ) : (
              <>
                En savoir plus
                <ChevronDown className="w-5 h-5" />
              </>
            )}
          </button>
        </motion.div>
      </div>
    </section>
  );
};

// Section Témoignages Vidéo avec Tom en vedette
const VideoTestimonialsSection = () => {
  const [showTomProofs, setShowTomProofs] = useState(false);
  
  // Tom est le premier, les autres suivent
  const tomTestimonial = VIDEO_TESTIMONIALS[0];
  const otherTestimonials = VIDEO_TESTIMONIALS.slice(1);

  return (
    <section 
      data-testid="video-testimonials-section"
      className="py-20 md:py-32 px-6"
    >
      <div className="max-w-7xl mx-auto">
        <motion.h2
          className="text-3xl md:text-4xl font-bold tracking-tight text-white text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Témoignages{" "}
          <span className="text-[#00A3FF]">Vidéo</span>
        </motion.h2>

        <motion.p
          className="text-lg text-gray-400 text-center mb-12 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Ils racontent leur expérience avec l'ASA
        </motion.p>

        {/* Tom en vedette tout en haut */}
        <div className="flex flex-col items-center mb-8">
          {/* Titre Tom */}
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2 text-center">
            {tomTestimonial.title}
          </h3>
          <p className="text-base text-[#00A3FF] mb-4">{tomTestimonial.subtitle}</p>
          
          {/* Vidéo Tom */}
          <div 
            className="relative w-[280px] aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_40px_rgba(0,163,255,0.1)] bg-black cursor-pointer group mb-4"
          >
            <video
              src={tomTestimonial.url}
              className="w-full h-full object-cover"
              playsInline
              loop
              controls
            />
          </div>

          {/* Bouton "En savoir plus" pour Tom */}
          <button
            data-testid="show-tom-proofs-btn"
            onClick={() => setShowTomProofs(!showTomProofs)}
            className="inline-flex items-center gap-2 px-6 py-2 rounded-full border border-[#00A3FF]/50 text-[#00A3FF] font-semibold hover:bg-[#00A3FF]/10 transition-all duration-300 text-sm"
          >
            {showTomProofs ? (
              <>
                Voir moins
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                En savoir plus
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </button>

          {/* Photos de preuves de Tom */}
          <AnimatePresence>
            {showTomProofs && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col md:flex-row gap-4 mt-6 w-full max-w-4xl"
              >
                {tomTestimonial.proofImages.map((img, idx) => (
                  <img 
                    key={idx}
                    src={img}
                    alt={`Preuve ${idx + 1}`}
                    className="w-full md:w-72 h-auto rounded-xl border border-white/10 object-contain hover:scale-105 transition-transform cursor-pointer shadow-lg bg-black"
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Les autres témoignages vidéo */}
        <div className="flex flex-wrap justify-center gap-8 mt-12">
          {otherTestimonials.map((video, index) => (
            <VideoTestimonial
              key={index}
              videoUrl={video.url}
              title={video.title}
              subtitle={video.subtitle}
              proofImages={[]}
              index={index + 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Section Vidéos YouTube en ligne horizontale
const YouTubeVideosSection = () => (
  <section 
    data-testid="youtube-videos-section"
    className="py-20 md:py-32 px-6 bg-white/[0.02]"
  >
    <div className="max-w-7xl mx-auto">
      <motion.h2
        data-testid="youtube-videos-title"
        className="text-3xl md:text-4xl font-bold tracking-tight text-white text-center mb-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        Découvre Mon{" "}
        <span className="text-[#00A3FF]">Parcours</span>
      </motion.h2>

      <motion.p
        className="text-lg text-gray-400 text-center mb-12 max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        Des vidéos qui te montrent la réalité du business en ligne
      </motion.p>

      {/* Grille horizontale de vidéos (2 vidéos) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {YOUTUBE_VIDEOS.map((video, index) => (
          <motion.div
            key={video.id}
            data-testid={`youtube-video-card-${index}`}
            className="flex flex-col"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
          >
            {/* Titre de la vidéo */}
            <h3 className="text-lg md:text-xl font-bold text-white mb-4 text-center">
              {video.title}
            </h3>
            {/* Embed YouTube */}
            <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_40px_rgba(0,163,255,0.1)] bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${video.id}?rel=0&modestbranding=1`}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full border-none"
              />
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="flex justify-center mt-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <CTAButton href={CONFIG.diagnosticUrl}>
          Recevoir mon diagnostic gratuit
        </CTAButton>
      </motion.div>
    </div>
  </section>
);

// Features Section
const FeaturesSection = () => (
  <section 
    data-testid="features-section"
    className="py-20 md:py-32 px-6"
  >
    <div className="max-w-7xl mx-auto">
      <motion.h2
        data-testid="features-title"
        className="text-3xl md:text-4xl font-bold tracking-tight text-white text-center mb-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        Pourquoi Avoir{" "}
        <span className="text-[#00A3FF]">Le Diagnostic Gratuit ?</span>
      </motion.h2>

      <motion.p
        className="text-lg text-gray-400 text-center mb-12 max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        Ce qui fait la différence avec tous les autres programmes
      </motion.p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
        {SELL_CARDS.map((card, index) => (
          <SellCard 
            key={index}
            icon={card.icon}
            title={card.title}
            description={card.description}
            index={index}
          />
        ))}
      </div>
    </div>
  </section>
);

// Disclaimer Section
const DisclaimerSection = () => (
  <section 
    data-testid="disclaimer-section"
    className="py-10 px-6 border-t border-white/10"
  >
    <div className="max-w-2xl mx-auto">
      <p className="text-xs text-gray-600 text-center uppercase tracking-widest">
        Disclaimer : Les résultats présentés sur cette page sont des témoignages réels de nos membres. 
        Les résultats individuels peuvent varier en fonction de l'effort, de l'engagement et de nombreux autres facteurs. 
        Ce programme ne garantit pas de gains financiers spécifiques.
      </p>
    </div>
  </section>
);

// Final CTA Section
const FinalCTASection = () => (
  <section 
    data-testid="final-cta-section"
    className="py-20 md:py-32 px-6 bg-gradient-to-b from-transparent to-[#00A3FF]/5"
  >
    <div className="max-w-4xl mx-auto text-center">
      <motion.h2
        data-testid="final-cta-title"
        className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        Prêt à Transformer{" "}
        <span className="text-[#00A3FF]">Ta Vie ?</span>
      </motion.h2>

      <motion.p
        className="text-lg md:text-xl text-gray-400 mb-10 max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        Rejoins les 500+ entrepreneurs qui ont déjà pris leur avenir en main
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <CTAButton href={CONFIG.diagnosticUrl} className="text-xl py-5 px-12">
          Réserver mon diagnostic gratuit
        </CTAButton>
      </motion.div>
    </div>
  </section>
);

// Footer Section
const FooterSection = () => (
  <footer 
    data-testid="footer-section"
    className="py-8 px-6 border-t border-white/10"
  >
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
      <img 
        src={CONFIG.logoUrl} 
        alt={`${CONFIG.programName} Logo`}
        className="w-16"
      />
      <p className="text-sm text-gray-600 text-center">
        © {new Date().getFullYear()} {CONFIG.fullName}. Tous droits réservés.
      </p>
    </div>
  </footer>
);

// Main Landing Page
const LandingPage = () => (
  <div data-testid="landing-page" className="min-h-screen bg-[#0a0a0a]">
    <HeroSection />
    <VSLSection />
    <VideoTestimonialsSection />
    <TestimonialsSection />
    <YouTubeVideosSection />
    <FeaturesSection />
    <FinalCTASection />
    <FooterSection />
  </div>
);

function App() {
  return (
    <div className="App">
      <HashRouter>
        <LandingPage />
      </HashRouter>
    </div>
  );
}

export default App;
